import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["zone"]

  connect() {
    this._draggedTile = null
    this._pendingDrag  = null  // { tile, startX, startY }
    this._touchDrag    = null  // { tile, ghost, activeZone, offsetX, offsetY }

    this._observer = new MutationObserver(() => this._makeTilesDraggable())
    this._observer.observe(this.element, { childList: true, subtree: true })
    this._makeTilesDraggable()

    this._docTouchMove = this._onDocumentTouchMove.bind(this)
    this._docTouchEnd  = this._onDocumentTouchEnd.bind(this)
    document.addEventListener("touchmove", this._docTouchMove, { passive: false })
    document.addEventListener("touchend",  this._docTouchEnd)
  }

  disconnect() {
    this._observer?.disconnect()
    document.removeEventListener("touchmove", this._docTouchMove)
    document.removeEventListener("touchend",  this._docTouchEnd)
  }

  // ── Desktop drag ────────────────────────────────────────────────────

  tileDragStart(event) {
    this._draggedTile = event.currentTarget.closest("[data-state]")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", "tile")
  }

  zoneDragOver(event) {
    if (event.dataTransfer.files.length > 0) return
    event.preventDefault()
    event.currentTarget.classList.add("border-warm-amber-500", "bg-hearth-600")
  }

  zoneDragLeave(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      event.currentTarget.classList.remove("border-warm-amber-500", "bg-hearth-600")
    }
  }

  zoneDrop(event) {
    if (event.dataTransfer.files.length > 0 || !this._draggedTile) return
    event.preventDefault()
    event.currentTarget.classList.remove("border-warm-amber-500", "bg-hearth-600")

    const zone = event.currentTarget.closest("[data-controller~='file-upload']")
    const previews = zone.querySelector("[data-file-upload-target='previews']")
    const fieldName = zone.dataset.fileUploadFieldNameValue

    previews.appendChild(this._draggedTile)

    const input = this._draggedTile.querySelector("input[type='hidden']")
    if (input) input.name = fieldName

    this._draggedTile = null
    this._refreshBucketStates()
  }

  // ── Touch drag ──────────────────────────────────────────────────────

  tileTouchStart(event) {
    if (event.touches.length !== 1) return
    const tile = event.currentTarget.closest("[data-state]")
    if (!tile) return
    const touch = event.touches[0]
    this._pendingDrag = { tile, startX: touch.clientX, startY: touch.clientY }
  }

  _onDocumentTouchMove(event) {
    const touch = event.touches[0]

    // Not yet dragging — prevent scroll and check if threshold crossed
    if (this._pendingDrag && !this._touchDrag) {
      event.preventDefault()
      const { tile, startX, startY } = this._pendingDrag
      if (Math.hypot(touch.clientX - startX, touch.clientY - startY) > 8) {
        this._beginTouchDrag(tile, touch)
        this._pendingDrag = null
      }
      return
    }

    if (!this._touchDrag) return
    event.preventDefault()

    const { ghost } = this._touchDrag
    ghost.style.left = `${touch.clientX - this._touchDrag.offsetX}px`
    ghost.style.top  = `${touch.clientY - this._touchDrag.offsetY}px`

    // elementFromPoint sees through the ghost (pointer-events: none)
    const el   = document.elementFromPoint(touch.clientX, touch.clientY)
    const zone = el?.closest("[data-document-buckets-target='zone']") ?? null

    if (zone !== this._touchDrag.activeZone) {
      this._touchDrag.activeZone?.classList.remove("border-warm-amber-500", "bg-hearth-600")
      zone?.classList.add("border-warm-amber-500", "bg-hearth-600")
      this._touchDrag.activeZone = zone
    }
  }

  _onDocumentTouchEnd() {
    this._pendingDrag = null
    if (!this._touchDrag) return

    const { tile, ghost, activeZone } = this._touchDrag
    ghost.remove()
    tile.style.opacity = ""
    activeZone?.classList.remove("border-warm-amber-500", "bg-hearth-600")

    if (activeZone) {
      const previews  = activeZone.querySelector("[data-file-upload-target='previews']")
      const fieldName = activeZone.dataset.fileUploadFieldNameValue
      if (previews) {
        previews.appendChild(tile)
        const input = tile.querySelector("input[type='hidden']")
        if (input) input.name = fieldName
        this._refreshBucketStates()
      }
    }

    this._touchDrag = null
  }

  _beginTouchDrag(tile, touch) {
    const rect  = tile.getBoundingClientRect()
    const ghost = tile.cloneNode(true)

    Object.assign(ghost.style, {
      position:      "fixed",
      width:         `${rect.width}px`,
      height:        `${rect.height}px`,
      left:          `${rect.left}px`,
      top:           `${rect.top}px`,
      opacity:       "0.9",
      pointerEvents: "none",
      zIndex:        "9999",
      transform:     "scale(1.06) rotate(1deg)",
      boxShadow:     "0 8px 32px rgba(0,0,0,0.5)",
      borderRadius:  "0.75rem",
    })

    document.body.appendChild(ghost)
    tile.style.opacity = "0.3"

    this._touchDrag = {
      tile,
      ghost,
      activeZone: null,
      offsetX: touch.clientX - rect.left,
      offsetY: touch.clientY - rect.top,
    }
  }

  // ── Shared ──────────────────────────────────────────────────────────

  _makeTilesDraggable() {
    this.element.querySelectorAll("[data-state]").forEach(tile => {
      tile.draggable = true
      if (!tile.dataset.action?.includes("tileDragStart")) {
        tile.setAttribute("data-action",
          (tile.dataset.action || "") + " dragstart->document-buckets#tileDragStart")
      }
      if (!tile.dataset.action?.includes("tileTouchStart")) {
        tile.setAttribute("data-action",
          (tile.dataset.action || "") + " touchstart->document-buckets#tileTouchStart")
      }
    })
  }

  _refreshBucketStates() {
    this.zoneTargets.forEach(zone => {
      const ctrl = this.application.getControllerForElementAndIdentifier(zone, "file-upload")
      ctrl?.updateDropzoneState()
    })
  }
}
